import {
  ConnectionModeEnum,
  ConnectionType,
  getUnifiedLiquiditySymbolsByMarket,
  HStack,
  Icon,
  IconName,
  IndicatorBadge,
  IndicatorBadgeSizes,
  ProductTypeEnum,
  SecurityTypesUtils,
  Text,
  useMarketAccountsContext,
  useSecuritiesContext,
  VStack,
  type Market,
  type MarketSecurityStatus,
  type Security,
  type UnifiedLiquidityToken,
} from '@talos/kyoko';
import { compact, uniq } from 'lodash-es';
import { useMemo } from 'react';
import { MULTILEG_MARKET_ACCOUNT } from '../../../providers/OMSContext.types';
import { getRFQSyntheticCrossSymbol } from '../../../utils/rfq';
import { AvailabilityEnum, type AvailabilityCondition, type AvailabilityResult } from '../types';

/** A hook wrapper for getMSSEnabledCondition */
export const useMSSEnabledCondition = ({
  connectionType,
  mssByAccountBySymbol,
  symbol,
  ulTokensForSymbol,
  justWarnOnFailure,
}: Omit<GetMSSEnabledConditionParams, 'securitiesBySymbol'>): AvailabilityCondition => {
  const { securitiesBySymbol } = useSecuritiesContext();

  return useMemo(
    () =>
      getMSSEnabledCondition({
        connectionType,
        mssByAccountBySymbol,
        symbol,
        ulTokensForSymbol,
        justWarnOnFailure,
        securitiesBySymbol,
      }),
    [connectionType, mssByAccountBySymbol, symbol, ulTokensForSymbol, securitiesBySymbol, justWarnOnFailure]
  );
};

export interface GetMSSEnabledConditionParams {
  connectionType: ConnectionType;
  /** The complete map of MSS records to base the condition checking on */
  mssByAccountBySymbol: Map<string, Map<string, MarketSecurityStatus>> | undefined;
  symbol: string | undefined;
  /** When using unified liquidity, pass the token list so an account stays selectable if any UL symbol is tradable */
  ulTokensForSymbol?: UnifiedLiquidityToken[];
  /** If true, will just Warn instead of Disabling the item on condition failure */
  justWarnOnFailure?: boolean;
  /** Basic refdata */
  securitiesBySymbol: Map<string, Security>;
}

/**
 * Returns an AvailabilityCondition used to check if a MarketSecurityStatus.Enabled is Up or not.
 *
 * If we at any point are not able to resolve an MSS, the condition will return available: true.
 */
export function getMSSEnabledCondition({
  connectionType,
  mssByAccountBySymbol,
  symbol,
  ulTokensForSymbol,
  justWarnOnFailure,
  securitiesBySymbol,
}: GetMSSEnabledConditionParams): AvailabilityCondition {
  const failureAvailability = justWarnOnFailure ? AvailabilityEnum.Warning : AvailabilityEnum.Disabled;
  const getSymbolsToCheck = getSymbolsToValidateForMarketFn(
    symbol,
    ulTokensForSymbol,
    securitiesBySymbol,
    connectionType
  );
  const ulTokenSymbols = uniq(ulTokensForSymbol?.map(token => token.Symbol) ?? []);

  return {
    id: `mss-enabled`,
    ready: mssByAccountBySymbol != null && symbol != null,
    condition: (market, marketAccount) => {
      if (!symbol) {
        // This is just a typing artefact. If there is no symbol, we will not be given any markets to validate.
        return { availability: AvailabilityEnum.Ok };
      }

      if (!mssByAccountBySymbol) {
        // If we fail to load the map at all, we default to showing all.
        return { availability: AvailabilityEnum.Ok };
      }

      if (!marketAccount) {
        // MarketSecurityStatuses are only applicable for market accounts
        return { availability: AvailabilityEnum.Ok };
      }

      if (marketAccount.Name === MULTILEG_MARKET_ACCOUNT) {
        // For multileg specifically, the backend doesn't do any MSS Status=Enabled validation, so we don't either. Yes, its insane.
        return { availability: AvailabilityEnum.Ok };
      }

      const security = securitiesBySymbol.get(symbol);
      if (SecurityTypesUtils.Compound.isProductType(security)) {
        // Compound securities do not generate MSS records, so no validation on them.
        return { availability: AvailabilityEnum.Ok };
      }

      const results: MSSConditionSymbolLevelResult[] = getSymbolsToCheck(market).map(symbol => {
        const mss = mssByAccountBySymbol.get(marketAccount.Name)?.get(symbol);

        if (!mss) {
          return { symbol, availability: failureAvailability, failedReason: 'No MSS found' };
        }

        return mss.Enabled === ConnectionModeEnum.Up
          ? { symbol, availability: AvailabilityEnum.Ok }
          : { symbol, availability: failureAvailability, failedReason: 'Symbol disabled' };
      });

      const accountLevelAvailability = getMSSConditionAccountLevelAvailability(results, symbol);
      const displayResults = getMSSConditionDisplayResults(results, { selectedSymbol: symbol, ulTokenSymbols });

      if (accountLevelAvailability === AvailabilityEnum.Ok) {
        return { availability: AvailabilityEnum.Ok };
      }

      return {
        availability: accountLevelAvailability,
        render: () => (
          <MSSConditionInfo
            accountName={marketAccount.Name}
            selectedSymbol={symbol}
            symbolLevelResults={results}
            ulTokenSymbols={ulTokenSymbols}
          />
        ),
        _text: getMSSConditionText(displayResults),
      } satisfies AvailabilityResult;
    },
  };
}

export function MSSConditionInfo({
  accountName,
  selectedSymbol,
  symbolLevelResults,
  ulTokenSymbols,
}: {
  accountName: string;
  selectedSymbol: string;
  symbolLevelResults: MSSConditionSymbolLevelResult[];
  ulTokenSymbols?: string[];
}) {
  const { securitiesBySymbol } = useSecuritiesContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const marketAccount = marketAccountsByName.get(accountName);

  const displaySymbolLevelResults = getMSSConditionDisplayResults(symbolLevelResults, {
    selectedSymbol,
    ulTokenSymbols,
  });
  const someResultHasDifferentSymbol = displaySymbolLevelResults.some(result => result.symbol !== selectedSymbol);
  const showPerSymbolFailureReason = someResultHasDifferentSymbol || displaySymbolLevelResults.length > 1;

  if (!showPerSymbolFailureReason) {
    const result = displaySymbolLevelResults.at(0);
    return result && 'failedReason' in result ? result.failedReason : undefined;
  }

  // Multi-symbol validation rendering. Used for cases such as RFQ Synthetic Crossing
  return (
    <VStack gap="spacing4" minWidth="220px" alignItems="flex-start" p="spacing2">
      <Text fontWeight="bold" color="colorTextSubtle">
        {marketAccount?.DisplayName ?? accountName}
      </Text>
      {displaySymbolLevelResults.map(result => {
        const security = securitiesBySymbol.get(result.symbol);
        return (
          <HStack w="100%" key={result.symbol} justifyContent="space-between" gap="spacing12">
            <HStack gap="spacing2">
              <Text color="colorTextAttention">{security?.DisplaySymbol ?? result.symbol}</Text>
              {result.availability < AvailabilityEnum.Ok && <Icon icon={IconName.DotSolid} color="yellow.lighten" />}
            </HStack>

            <IndicatorBadge size={IndicatorBadgeSizes.Small}>
              <HStack gap="spacing2" alignItems="flex-start">
                <Icon icon={result.availability === AvailabilityEnum.Ok ? IconName.Check : IconName.Close} />
                {'failedReason' in result && <Text>{result.failedReason}</Text>}
              </HStack>
            </IndicatorBadge>
          </HStack>
        );
      })}
    </VStack>
  );
}

export function getMSSConditionDisplayResults(
  results: MSSConditionSymbolLevelResult[],
  options?: { selectedSymbol?: string; ulTokenSymbols?: string[] }
): MSSConditionSymbolLevelResult[] {
  const failed = results.filter(result => result.availability < AvailabilityEnum.Ok);
  const unselectedUlSymbols = new Set(
    (options?.ulTokenSymbols ?? []).filter(symbol => symbol !== options?.selectedSymbol)
  );
  const withoutUnselectedUlFailures = failed.filter(result => !unselectedUlSymbols.has(result.symbol));
  // Empty after omitting UL-only gaps means those tokens were the only checked symbols — keep them.
  return withoutUnselectedUlFailures.length > 0 ? withoutUnselectedUlFailures : failed;
}

export type MSSConditionSymbolLevelResult =
  | {
      symbol: string;
      availability: typeof AvailabilityEnum.Ok;
    }
  | {
      symbol: string;
      availability: typeof AvailabilityEnum.Disabled | typeof AvailabilityEnum.Warning;
      failedReason: string;
    };

/**
 * Generates simplified text from MSS condition results for testing purposes.
 */
export function getMSSConditionText(results: MSSConditionSymbolLevelResult[]): string {
  const failed = results.filter(
    (r): r is MSSConditionSymbolLevelResult & { failedReason: string } => 'failedReason' in r
  );
  if (failed.length === 0) {
    return '';
  }
  if (failed.length === 1) {
    return failed[0].failedReason;
  }
  return `${failed.length} symbols have issues`;
}

/**
 * Account-level availability from per-symbol MSS results.
 *
 * UL tokens keep an account selectable when any checked symbol is tradable.
 * The yellow-dot warning is only for a failed selected symbol. UL-only gaps stay
 * on the UL icon.
 */
export function getMSSConditionAccountLevelAvailability(
  symbolLevelResults: MSSConditionSymbolLevelResult[],
  selectedSymbol: string
): AvailabilityEnum {
  if (symbolLevelResults.length === 0) {
    return AvailabilityEnum.Ok;
  }

  const anyTradable = symbolLevelResults.some(result => result.availability === AvailabilityEnum.Ok);
  const selectedResult = symbolLevelResults.find(result => result.symbol === selectedSymbol);
  const selectedFailed = selectedResult != null && selectedResult.availability < AvailabilityEnum.Ok;

  if (!anyTradable) {
    return symbolLevelResults.some(result => result.availability === AvailabilityEnum.Disabled)
      ? AvailabilityEnum.Disabled
      : AvailabilityEnum.Warning;
  }

  return selectedFailed ? AvailabilityEnum.Warning : AvailabilityEnum.Ok;
}

/**
 * Returns the symbols an MSS condition should validate for a market.
 *
 * UL tokens are included so an account stays selectable when a unified symbol is
 * tradable. Warning display is filtered separately to the selected symbol.
 * RFQ synthetic crossing remains the other multi-symbol case.
 */
export const getSymbolsToValidateForMarketFn = (
  symbol: string | undefined,
  ulTokensForSymbol: UnifiedLiquidityToken[] | undefined,
  securitiesBySymbol: Map<string, Security>,
  connectionType: ConnectionType
): ((market: Market) => string[]) => {
  const ulSymbolsByMarket = ulTokensForSymbol ? getUnifiedLiquiditySymbolsByMarket(ulTokensForSymbol) : undefined;
  const security = symbol ? securitiesBySymbol.get(symbol) : undefined;
  const securityMarketsSet = new Set(security?.Markets ?? []);
  const ulSymbols = uniq(ulTokensForSymbol?.map(token => token.Symbol) ?? []);

  return (market: Market) => {
    if (!security) {
      return [];
    }

    if (security.ProductType === ProductTypeEnum.Spot) {
      if (connectionType === ConnectionType.RFQ && !securityMarketsSet.has(market.Name)) {
        // If there is some market selected which is not in the security's set for Spot, then this market is selected via RFQ Synthetic Crossing.
        return compact([getRFQSyntheticCrossSymbol(security)]);
      }

      return uniq([security.Symbol, ...ulSymbols]);
    }

    const ulSymbolsForMarket = ulSymbolsByMarket?.get(market.Name);
    const symbols: string[] = ulSymbolsForMarket ? [...ulSymbolsForMarket] : [];

    const includeBaseSymbol = security.Markets?.includes(market.Name);
    if (includeBaseSymbol) {
      symbols.unshift(security.Symbol);
    }

    return symbols;
  };
};
